import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import api from '../services/api';
import { useFamily } from '../contexts/FamilyContext';
import { useAuth } from '../contexts/AuthContext';

const GiftTracker = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentFamily } = useFamily();
  const [records, setRecords] = useState([]);
  const [events, setEvents] = useState([]);
  const [groups, setGroups] = useState([]);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [formData, setFormData] = useState({
    receiver_name: '',
    gift_description: '',
    event: '',
    name_picker_group: '',
    wishlist_item: '',
    year: new Date().getFullYear(),
  });

  const fetchRecords = useCallback(async () => {
    if (!currentFamily) return;
    try {
      const response = await api.get(`/gift-records/?family_id=${currentFamily.id}`);
      setRecords(response.data.results || response.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }, [currentFamily]);

  const fetchEvents = useCallback(async () => {
    if (!currentFamily) return;
    try {
      const response = await api.get(`/events/?family_id=${currentFamily.id}`);
      const eventsData = response.data.results || response.data;
      setEvents(Array.isArray(eventsData) ? eventsData : []);
    } catch (error) {
      setEvents([]);
    }
  }, [currentFamily]);

  const fetchGroups = useCallback(async () => {
    if (!currentFamily) return;
    try {
      const response = await api.get(`/name-picker/?family_id=${currentFamily.id}`);
      const groupsData = response.data.results || response.data;
      setGroups(Array.isArray(groupsData) ? groupsData : []);
    } catch (error) {
      setGroups([]);
    }
  }, [currentFamily]);

  const fetchWishlists = useCallback(async () => {
    if (!currentFamily) return;
    try {
      const response = await api.get(`/wishlists/?family_id=${currentFamily.id}`);
      const wishlistsData = response.data.results || response.data;
      
      // Flatten all wishlist items into a single array
      const allItems = [];
      if (Array.isArray(wishlistsData)) {
        wishlistsData.forEach(wishlist => {
          if (wishlist.items && Array.isArray(wishlist.items)) {
            wishlist.items.forEach(item => {
              allItems.push({
                ...item,
                wishlist_title: wishlist.title,
                wishlist_user_name: wishlist.user_name
              });
            });
          }
        });
      }
      setWishlistItems(allItems);
    } catch (error) {
      setWishlistItems([]);
    }
  }, [currentFamily]);

  useEffect(() => {
    if (currentFamily) {
      fetchRecords();
      fetchEvents();
      fetchGroups();
      fetchWishlists();
    } else {
      setLoading(false);
    }
  }, [currentFamily, fetchRecords, fetchEvents, fetchGroups, fetchWishlists]);

  const handleCreate = async () => {
    if (!currentFamily) {
      alert('Please select or create a family first');
      return;
    }
    try {
      const response = await api.post('/gift-records/', {
        ...formData,
        family_id: currentFamily.id,
        event: formData.event || null,
        name_picker_group: formData.name_picker_group || null,
        wishlist_item: formData.wishlist_item || null,
      });
      setRecords([response.data, ...records]);
      setOpen(false);
      setFormData({
        receiver_name: '',
        gift_description: '',
        event: '',
        name_picker_group: '',
        wishlist_item: '',
        year: new Date().getFullYear(),
      });
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to create record');
    }
  };

  const handleEditClick = (record) => {
    setEditingRecord(record);
    // Handle event - it might be an ID or an object
    const eventId = typeof record.event === 'object' ? record.event.id : record.event;
    const namePickerGroupId = typeof record.name_picker_group === 'object' ? record.name_picker_group.id : record.name_picker_group;
    const wishlistItemId = typeof record.wishlist_item === 'object' ? record.wishlist_item.id : record.wishlist_item;
    
    setFormData({
      receiver_name: record.receiver_name || '',
      gift_description: record.gift_description || '',
      event: eventId || '',
      name_picker_group: namePickerGroupId || '',
      wishlist_item: wishlistItemId || '',
      year: record.year || new Date().getFullYear(),
    });
    setEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!currentFamily || !editingRecord) return;
    try {
      const response = await api.put(`/gift-records/${editingRecord.id}/`, {
        ...formData,
        family_id: currentFamily.id,
        event: formData.event || null,
        name_picker_group: formData.name_picker_group || null,
        wishlist_item: formData.wishlist_item || null,
      });
      setRecords(records.map(r => r.id === editingRecord.id ? response.data : r));
      setEditDialogOpen(false);
      setEditingRecord(null);
      setFormData({
        receiver_name: '',
        gift_description: '',
        event: '',
        name_picker_group: '',
        wishlist_item: '',
        year: new Date().getFullYear(),
      });
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to update record');
    }
  };

  const handleDeleteClick = (record) => {
    setEditingRecord(record);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!editingRecord) return;
    try {
      await api.delete(`/gift-records/${editingRecord.id}/`);
      setRecords(records.filter(r => r.id !== editingRecord.id));
      setDeleteDialogOpen(false);
      setEditingRecord(null);
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to delete record');
    }
  };

  const groupedByYear = records.reduce((acc, record) => {
    if (!acc[record.year]) {
      acc[record.year] = [];
    }
    acc[record.year].push(record);
    return acc;
  }, {});

  // Check if user is admin
  const userMember = currentFamily?.members?.find((m) => m.user_id === user?.uid);
  const isAdmin = userMember && ['owner', 'admin'].includes(userMember.role);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Gift Tracker</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpen(true)}
        >
          Add Gift Record
        </Button>
      </Box>

      {!currentFamily ? (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              No Family Selected
            </Typography>
            <Typography>
              Please create or join a family to start tracking gifts.
            </Typography>
            <Button
              variant="contained"
              sx={{ mt: 2 }}
              onClick={() => navigate('/families')}
            >
              Go to Families
            </Button>
          </CardContent>
        </Card>
      ) : loading ? (
        <Typography>Loading...</Typography>
      ) : records.length === 0 ? (
        <Card>
          <CardContent>
            <Typography>Only you can see your gift tracker. No gift records yet. Track your first gift!</Typography>
          </CardContent>
        </Card>
      ) : (
        Object.keys(groupedByYear)
          .sort((a, b) => b - a)
          .map((year) => (
            <Box key={year} sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>
                {year}
              </Typography>
              <Grid container spacing={2}>
                {groupedByYear[year].map((record) => (
                  <Grid item xs={12} md={6} key={record.id}>
                    <Card>
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="start">
                          <Box flex={1}>
                            <Typography variant="h6" gutterBottom>
                              To: {record.receiver_name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              From: {record.giver_name}
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 1 }}>
                              {record.gift_description}
                            </Typography>
                            {record.event && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Event: {record.event}
                              </Typography>
                            )}
                            {record.wishlist_item_name && (
                              <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                                From Wishlist: {record.wishlist_item_name}
                              </Typography>
                            )}
                          </Box>
                          {/* Show edit/delete buttons if user is admin or record owner */}
                          {(isAdmin || record.giver_id === user?.uid) && (
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={() => handleEditClick(record)}
                                color="primary"
                                aria-label="Edit gift record"
                                sx={{ 
                                  '&:hover': { 
                                    bgcolor: 'primary.light',
                                    color: 'white'
                                  }
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteClick(record)}
                                color="error"
                                aria-label="Delete gift record"
                                sx={{ 
                                  '&:hover': { 
                                    bgcolor: 'error.light',
                                    color: 'white'
                                  }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Gift Record</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Receiver Name"
            margin="normal"
            value={formData.receiver_name}
            onChange={(e) =>
              setFormData({ ...formData, receiver_name: e.target.value })
            }
          />
          <TextField
            fullWidth
            label="Gift Description"
            margin="normal"
            multiline
            rows={3}
            value={formData.gift_description}
            onChange={(e) =>
              setFormData({ ...formData, gift_description: e.target.value })
            }
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Event (optional)</InputLabel>
            <Select
              value={formData.event || ''}
              onChange={(e) => setFormData({ ...formData, event: e.target.value })}
            >
              <MenuItem value="">None</MenuItem>
              {events && events.length > 0 ? (
                events.map((event) => (
                  <MenuItem key={event.id} value={event.id}>
                    {event.title}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>No events available</MenuItem>
              )}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Name Picker Group (optional)</InputLabel>
            <Select
              value={formData.name_picker_group || ''}
              onChange={(e) =>
                setFormData({ ...formData, name_picker_group: e.target.value })
              }
            >
              <MenuItem value="">None</MenuItem>
              {groups && groups.length > 0 ? (
                groups.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>No name picker groups available</MenuItem>
              )}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Wishlist Item (optional)</InputLabel>
            <Select
              value={formData.wishlist_item || ''}
              onChange={(e) => setFormData({ ...formData, wishlist_item: e.target.value })}
            >
              <MenuItem value="">None</MenuItem>
              {wishlistItems && wishlistItems.length > 0 ? (
                wishlistItems.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name} {item.wishlist_title ? `(from ${item.wishlist_user_name}'s ${item.wishlist_title})` : ''}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>No wishlist items available</MenuItem>
              )}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Year"
            type="number"
            margin="normal"
            value={formData.year}
            onChange={(e) =>
              setFormData({ ...formData, year: parseInt(e.target.value) })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained">
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editDialogOpen} onClose={() => {
        setEditDialogOpen(false);
        setEditingRecord(null);
        setFormData({
          receiver_name: '',
          gift_description: '',
          event: '',
          name_picker_group: '',
          wishlist_item: '',
          year: new Date().getFullYear(),
        });
      }} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Gift Record</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Receiver Name"
            margin="normal"
            value={formData.receiver_name}
            onChange={(e) =>
              setFormData({ ...formData, receiver_name: e.target.value })
            }
          />
          <TextField
            fullWidth
            label="Gift Description"
            margin="normal"
            multiline
            rows={3}
            value={formData.gift_description}
            onChange={(e) =>
              setFormData({ ...formData, gift_description: e.target.value })
            }
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Event (optional)</InputLabel>
            <Select
              value={formData.event || ''}
              onChange={(e) => setFormData({ ...formData, event: e.target.value })}
            >
              <MenuItem value="">None</MenuItem>
              {events && events.length > 0 ? (
                events.map((event) => (
                  <MenuItem key={event.id} value={event.id}>
                    {event.title}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>No events available</MenuItem>
              )}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Name Picker Group (optional)</InputLabel>
            <Select
              value={formData.name_picker_group || ''}
              onChange={(e) =>
                setFormData({ ...formData, name_picker_group: e.target.value })
              }
            >
              <MenuItem value="">None</MenuItem>
              {groups && groups.length > 0 ? (
                groups.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>No name picker groups available</MenuItem>
              )}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Wishlist Item (optional)</InputLabel>
            <Select
              value={formData.wishlist_item || ''}
              onChange={(e) => setFormData({ ...formData, wishlist_item: e.target.value })}
            >
              <MenuItem value="">None</MenuItem>
              {wishlistItems && wishlistItems.length > 0 ? (
                wishlistItems.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name} {item.wishlist_title ? `(from ${item.wishlist_user_name}'s ${item.wishlist_title})` : ''}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>No wishlist items available</MenuItem>
              )}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Year"
            type="number"
            margin="normal"
            value={formData.year}
            onChange={(e) =>
              setFormData({ ...formData, year: parseInt(e.target.value) })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditDialogOpen(false);
            setEditingRecord(null);
            setFormData({
              receiver_name: '',
              gift_description: '',
              event: '',
              name_picker_group: '',
              wishlist_item: '',
              year: new Date().getFullYear(),
            });
          }}>Cancel</Button>
          <Button onClick={handleUpdate} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => {
        setDeleteDialogOpen(false);
        setEditingRecord(null);
      }} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Gift Record</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this gift record? This action cannot be undone.
          </Typography>
          {editingRecord && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>To:</strong> {editingRecord.receiver_name}
              </Typography>
              <Typography variant="body2">
                <strong>Gift:</strong> {editingRecord.gift_description}
              </Typography>
              <Typography variant="body2">
                <strong>Year:</strong> {editingRecord.year}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDeleteDialogOpen(false);
            setEditingRecord(null);
          }}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default GiftTracker;
